<template>
    <div :id="tunes?.anchorTune?.anchor" class="header-block" :class="{ 'before-list': beforeList }">
        <a v-if="setAnchor" :id="slugify(data.text)" class="heading-anchor" />
        <component :is="tag" :id="id" class="heading">
            <a-dangerous-html :content="data.text" tag="span" />
        </component>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import ADangerousHtml from '@core/components/dangerous-html/dangerous-html.vue';
import type { BodyVariant, HeaderData, HeaderTunes } from '@model/editor-js/blocks';
import commonUtils from '@utils/common';

defineOptions({
    name: 'BlockHeader',
});

const props = defineProps<{
    id?: string;
    data: HeaderData;
    tunes?: HeaderTunes;
    variant: BodyVariant;
    /** the next block is a list (resource & legal tighten the gap) */
    beforeList?: boolean;
    /** legal pages anchor headings by their text for the side navigation */
    setAnchor?: boolean;
}>();

// h1 is reserved for the page title; migrated content still has level 1
const tag = computed(() => `h${Math.max(props.data.level || 2, 2)}`);

const slugify = (text: string): string => commonUtils.slugify(text);
</script>

<style lang="pcss" scoped>
.variant-blog .heading {
    @mixin display;
    font-weight: 700;
    margin-top: 24px;
    margin-bottom: 16px;
    color: var(--av-nav-primary);

    &:is(h3, h4, h5, h6) {
        @mixin lead;
        font-weight: 700;
        margin-top: 16px;
        margin-bottom: 16px;
    }

    &:is(h3) {
        @mixin title;
    }
}

.variant-tru .heading {
    @mixin title-accent;
    margin: 24px 0;
    color: #000;

    &:is(h3, h4, h5, h6) {
        @mixin lead-accent;
        margin-top: 16px;
        margin-bottom: 24px;
    }
}

.variant-tru > .header-block:first-child .heading {
    margin-top: 0;
}

.variant-resource .header-block,
.variant-legal .header-block {
    font-weight: 700;
    color: var(--av-fixed-primary);
}

.variant-resource .heading:is(h2),
.variant-legal .heading:is(h2) {
    margin-top: 24px;
    font-size: 24px;
    line-height: 32px;
}

/* legal never styled h4–h6 */
.variant-resource .heading:is(h3, h4, h5, h6),
.variant-legal .heading:is(h3) {
    margin-top: 16px;
    font-size: 16px;
    line-height: 24px;
    margin-bottom: -16px;
}

.variant-resource .before-list .heading:is(h3),
.variant-legal .before-list .heading:is(h3) {
    margin-bottom: 0;
}

.variant-event-about .heading {
    margin-top: 0;
    color: var(--av-nav-primary);
    font-weight: 400;

    &:is(h2) {
        @mixin display;
        margin-bottom: 16px;
    }

    &:is(h3) {
        @mixin title;
        margin-bottom: 16px;
    }

    &:is(h4, h5, h6) {
        @mixin lead-accent;
        margin-bottom: 8px;
    }
}

.variant-event-compact .heading {
    @mixin paragraph-accent;
    color: var(--av-fixed-primary);
    margin-bottom: 16px;
}
</style>
